import React, { useState, useMemo } from 'react';
import Layout from '../layout/Layout';
import Section from '../ui/Section';
import Container from '../ui/Container';
import Button from '../ui/Button';
import Sparkle from '../ui/Sparkle';
import Eyebrow from '../ui/Eyebrow';
import { helpArticles, HELP_CATEGORIES, articlesByCategory } from '../../content/library';
import { CONTACT } from '../layout/siteData';

const HelpCenter = () => {
  const [q, setQ] = useState('');
  const grouped = useMemo(() => articlesByCategory(), []);
  const query = q.trim().toLowerCase();
  const filtered = query ? helpArticles.filter((a) => a.title.toLowerCase().includes(query)) : null;

  return (
    <Layout>
      <section className="relative overflow-hidden bg-sky text-center">
        <Sparkle size={120} color="#95a8e6" className="pointer-events-none absolute top-[12%] left-[9%] opacity-25 hidden sm:block" />
        <Sparkle size={56} color="#ffb442" className="pointer-events-none absolute bottom-[14%] right-[10%] opacity-80" />
        <Container className="relative py-14 md:py-20">
          <Eyebrow className="justify-center">Help Center</Eyebrow>
          <h1 className="mt-4 text-h1 font-extrabold text-ink">How can we help?</h1>
          <p className="mt-4 mx-auto max-w-[48ch] text-lead text-body">Guides and answers to help you get the most out of Shopstar.</p>
          <div className="flex items-center gap-3 max-w-[560px] mx-auto mt-8 bg-white rounded-full pl-6 pr-1.5 py-1.5 shadow-card text-muted">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" /><path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
            <input type="search" placeholder="Search help articles…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search help articles" data-testid="help-search" className="flex-1 border-none outline-none bg-transparent font-sans text-[1.05rem] text-ink py-3.5" />
          </div>
        </Container>
      </section>

      <Section tone="white" pad="lg">
        {filtered ? (
          <div>
            <h2 className="text-2xl font-extrabold text-ink mb-6">{filtered.length} result{filtered.length === 1 ? '' : 's'} for “{q}”</h2>
            {filtered.length === 0 ? (
              <p className="text-lead">No articles matched. Try another search, or <a href={`mailto:${CONTACT.email}`} className="text-ink font-bold">email our team</a>.</p>
            ) : (
              <ul className="grid sm:grid-cols-2 gap-3">
                {filtered.map((a) => (
                  <li key={a.slug}>
                    <a href={`/help-center/${a.slug}`} className="flex items-center gap-3 p-5 bg-cream border border-line rounded-2xl hover:border-brand hover:-translate-y-0.5 transition-all">
                      <Sparkle size={14} color="#95a8e6" />
                      <span className="font-bold text-ink">{a.title}</span>
                      <em className="ml-auto not-italic text-xs text-muted">{a.category}</em>
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-x-14 gap-y-8">
            {HELP_CATEGORIES.map((cat) => (grouped[cat] && grouped[cat].length > 0 && (
              <div key={cat}>
                <h2 className="flex items-center gap-2.5 text-xl font-extrabold text-ink pb-4 mb-2 border-b-2 border-line"><Sparkle size={18} color="#95a8e6" />{cat}</h2>
                <ul>
                  {grouped[cat].map((a) => (
                    <li key={a.slug}>
                      <a href={`/help-center/${a.slug}`} className="group flex items-center justify-between gap-3.5 py-3.5 px-1 font-semibold text-body border-b border-cream hover:text-ink hover:pl-2.5 transition-all">
                        {a.title}
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="text-ink flex-none opacity-0 group-hover:opacity-100 transition-opacity"><path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )))}
          </div>
        )}
      </Section>

      <Section tone="cream" pad="md">
        <div className="flex items-center justify-between gap-9 flex-wrap">
          <div>
            <Eyebrow>Still stuck?</Eyebrow>
            <h2 className="mt-3 text-[clamp(1.5rem,3vw,2.2rem)] font-extrabold text-ink">Our team is here to help.</h2>
            <p className="mt-2 text-body max-w-[48ch]">Reach our 100% South African Customer Success Team via email, WhatsApp or live chat.</p>
          </div>
          <div className="flex gap-3.5 flex-wrap">
            <Button href={`mailto:${CONTACT.email}`} icon={false}>Email us</Button>
            <Button href="/contact" variant="secondary" icon={false}>Contact page</Button>
          </div>
        </div>
      </Section>
    </Layout>
  );
};

export default HelpCenter;
